import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { Account } from "../api/types";

/**
 * The signed-in account: who it is, signing out, and deleting it.
 *
 * Both actions end on a full reload of the library rather than patching the
 * caches: everything on screen belonged to the account that just left.
 */
export function AccountMenu({ account }: { account: Account }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // A click anywhere else closes the menu, and forgets a half-made decision.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) {
        setOpen(false);
        setConfirming(false);
      }
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const leave = async (action: () => Promise<void>, failure: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      window.location.assign("/");
    } catch {
      setError(failure);
      setBusy(false);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-white/10"
      >
        <img
          src={account.avatarUrl}
          alt=""
          referrerPolicy="no-referrer"
          className="h-7 w-7 rounded-full bg-white/10"
        />
        <span className="max-w-40 truncate">{account.displayName || account.username}</span>
        <span className="text-[10px] opacity-50">▾</span>
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 rounded-lg border border-white/10 bg-neutral-900 p-1 text-sm shadow-xl">
          <div className="px-3 py-2 text-xs opacity-60">
            Connecté avec Discord en tant que <span className="font-mono">@{account.username}</span>
          </div>
          <button
            onClick={() => leave(api.logout, "la déconnexion a échoué")}
            disabled={busy}
            className="w-full rounded px-3 py-2 text-left hover:bg-white/10 disabled:opacity-50"
          >
            Se déconnecter
          </button>
          {confirming ? (
            <div className="rounded bg-red-500/10 px-3 py-2">
              <p className="text-xs text-red-200">
                Le compte, tous ses replays, leurs tracés et leurs liens de partage seront
                supprimés définitivement.
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => leave(api.deleteAccount, "la suppression a échoué")}
                  disabled={busy}
                  className="rounded bg-red-500/80 px-2 py-1 text-xs font-medium text-white hover:bg-red-500 disabled:opacity-50"
                >
                  Supprimer
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                  className="rounded px-2 py-1 text-xs hover:bg-white/10"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="w-full rounded px-3 py-2 text-left text-red-300 hover:bg-red-500/10"
            >
              Supprimer mon compte…
            </button>
          )}
          {error && <p className="px-3 py-1 text-xs text-red-400">{error}</p>}
        </div>
      )}
    </div>
  );
}
